import _ from 'underscore';
import { UniversalMathHelper } from '@biteinc/helpers-universal';
/**
 * @description
 * `price` is the term used for price in cents. $12.34's `price` is 1234
 * `float5` is used to store decimals as ints. Store with a factor of 10^5
 * 8.875% in float5 is 8875. $10.8875 in float5 is 1088750
 */
export const divideValueBy10ToXAndCeil = UniversalMathHelper.divideValueBy10ToXAndCeil;
const float5FromPrice = UniversalMathHelper.float5FromPrice;
export const priceFromFloat5 = UniversalMathHelper.priceFromFloat5;
const float5RoundedToNearestCent = UniversalMathHelper.float5RoundedToNearestCent;
export const getFloat5Rate = UniversalMathHelper.getFloat5Rate;
export const multiplyPriceByFloat5Rate = UniversalMathHelper.multiplyPriceByFloat5Rate;
export const getPercentageOfPrice = UniversalMathHelper.getPercentageOfPrice;
export const getTaxAmount = UniversalMathHelper.getTaxAmount;
function getIncludedTaxAmount(price, float5Rate) {
    const priceF5 = float5FromPrice(price);
    return Math.round((priceF5 * float5Rate) / (100000 + float5Rate));
}
export function getIncludedTaxAmounts(price, float5Rates) {
    const totalFloat5Rate = float5Rates.reduce((total, float5Rate) => total + float5Rate, 0);
    const taxTotalF5 = getIncludedTaxAmount(price, totalFloat5Rate);
    if (float5Rates.length === 1) {
        return [taxTotalF5];
    }
    // We should first round the total amount to the nearest cent to make sure
    // that the state gets its due. Then that rounded amount should get split
    // into individual tax amounts.
    const roundedTaxTotalF5 = float5RoundedToNearestCent(taxTotalF5);
    let remainingTaxTotalF5 = roundedTaxTotalF5;
    return float5Rates.map((float5Rate, i) => {
        if (i === float5Rates.length - 1) {
            return remainingTaxTotalF5;
        }
        // Calculate individual tax rate as a proportion of the cumulative rate.
        // Round this rate to the nearest cent to guarantee that the sum of all
        // individual rates once rounded adds up to the total.
        let amountF5 = (float5Rate / totalFloat5Rate) * roundedTaxTotalF5;
        amountF5 = float5RoundedToNearestCent(Math.round(amountF5));
        remainingTaxTotalF5 -= amountF5;
        return amountF5;
    });
}
export function float5FromFloatString(floatString) {
    if (floatString === '0') {
        return 0;
    }
    const periodIndex = floatString.indexOf('.');
    let neededZeroCount = 5;
    if (periodIndex >= 0) {
        if (floatString.slice(0, periodIndex) === '0') {
            // Take the first 5 digits after the period;
            floatString = floatString.substr(periodIndex + 1, 5);
            neededZeroCount = 5 - floatString.length;
            while (floatString.charAt(0) === '0') {
                floatString = floatString.substr(1);
            }
            if (!floatString.length) {
                return 0;
            }
        }
        else {
            neededZeroCount = 5 - Math.min(5, floatString.length - periodIndex - 1);
            floatString = [floatString.slice(0, periodIndex), floatString.slice(periodIndex + 1)].join('');
        }
    }
    for (let i = 0; i < neededZeroCount; i++) {
        floatString += '0';
    }
    return parseInt(floatString, 10);
}
export function stringFromFloat5(float5) {
    if (0 === float5) {
        return '0';
    }
    let float5Str = `${float5}`;
    if (float5Str.length > 5) {
        const periodIndex = float5Str.length - 5;
        return [float5Str.slice(0, periodIndex), '.', float5Str.slice(periodIndex)].join('');
    }
    let ret = '0.';
    for (let i = 0; i < 5 - float5Str.length; i++) {
        ret += '0';
    }
    while (float5Str.length > 0 && float5Str.charAt(float5Str.length - 1) === '0') {
        float5Str = float5Str.substr(0, float5Str.length - 1);
    }
    return ret + float5Str;
}
export function floatStringFromPrice(price) {
    return stringFromFloat5(float5FromPrice(price));
}
export const displayPrice = UniversalMathHelper.displayPrice;
export function stringDollarsFromPrice(priceInCents) {
    return `${floatDollarsFromPrice(priceInCents).toFixed(2)}`;
}
export const floatDollarsFromPrice = UniversalMathHelper.floatDollarsFromPrice;
export const priceFromFloatDollars = UniversalMathHelper.priceFromFloatDollars;
export function priceFromStrDollars(strDollars) {
    const [dollars = '0', cents = ''] = strDollars.split('.');
    // Make sure we have a 2-digit cent value
    let padding = '';
    _.times(2 - cents.length, () => {
        padding += '0';
    });
    return parseInt(`${dollars}${cents.substring(0, 2)}${padding}`, 10);
}
export function avg(numbers) {
    if (numbers.length < 1) {
        return 0;
    }
    return (numbers.reduce((total, number) => {
        return total + number;
    }, 0) / numbers.length);
}
export function stDev(numbers) {
    if (numbers.length < 2) {
        return 0;
    }
    const numberAvg = avg(numbers);
    const sum = numbers.reduce((total, number) => {
        return total + (number - numberAvg) ** 2;
    }, 0);
    return Math.sqrt(sum / (numbers.length - 1));
}
export function floatRateToPercentStr(floatRate) {
    return (floatRate * 100.0).toFixed(2);
}
export function percentStr(num, denom) {
    return floatRateToPercentStr(num / denom);
}
/**
 * Splits the specified amount into the specified shares.
 * @param {number} amountInCents
 * @param {number[]} percentages percentages that must add up to 1.
 * @returns {number[]} a list of integers that correspond to the
 * percentages of the original amount and that all add up to [amountInCents].
 */
export function splitAmount(amountInCents, percentages) {
    let usedAmount = 0;
    const splitAmounts = [];
    percentages.forEach((percentage, i) => {
        if (i === percentages.length - 1) {
            splitAmounts.push(amountInCents - usedAmount);
        }
        else {
            const amount = Math.floor(amountInCents * percentage);
            usedAmount += amount;
            splitAmounts.push(amount);
        }
    });
    return splitAmounts;
}
export const generateRandomCode = UniversalMathHelper.generateRandomCode;
// const EARTH_RADIUS_METERS = 6371000;
const EARTH_RADIUS_MILES = 3959;
function degreesToRadians(degrees) {
    return (Math.PI * degrees) / 180;
}
function haversine(radians) {
    return Math.pow(Math.sin(radians / 2), 2);
}
// https://en.wikipedia.org/wiki/Haversine_formula
function haversineDistance(coordinatesA, coordinatesB) {
    const lat1 = degreesToRadians(coordinatesA.lat);
    const lat2 = degreesToRadians(coordinatesB.lat);
    const deltaLat = degreesToRadians(coordinatesB.lat - coordinatesA.lat);
    const deltaLng = degreesToRadians(coordinatesB.lng - coordinatesA.lng);
    const centralAngleHaversine = haversine(deltaLat) + Math.cos(lat1) * Math.cos(lat2) * haversine(deltaLng);
    const centralAngle = 2 * Math.atan2(Math.sqrt(centralAngleHaversine), Math.sqrt(1 - centralAngleHaversine));
    // centralAngleHaversine === haversine(centralAngle)
    return EARTH_RADIUS_MILES * centralAngle;
}
export function distanceBetweenCoordinatesInMiles(coordinatesA, coordinatesB) {
    const latitudeA = coordinatesA[1];
    const longitudeA = coordinatesA[0];
    const latitudeB = coordinatesB[1];
    const longitudeB = coordinatesB[0];
    return haversineDistance({
        lat: latitudeA === undefined ? Number.NaN : latitudeA,
        lng: longitudeA === undefined ? Number.NaN : longitudeA,
    }, {
        lat: latitudeB === undefined ? Number.NaN : latitudeB,
        lng: longitudeB === undefined ? Number.NaN : longitudeB,
    });
}
export function quotientOrNull(numerator, denominator) {
    if (numerator === null || denominator === null || denominator === 0) {
        return null;
    }
    return numerator / denominator;
}
export function quotientOrZero(numerator, denominator) {
    if (numerator === null || denominator === null || denominator === 0) {
        return 0;
    }
    return numerator / denominator;
}
//# sourceMappingURL=math_helper.js.map